import { useLiveQuery } from "dexie-react-hooks";
import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { computeAccountingMonth } from "@/domain/accounting-period";
import type { AttachmentOrigin } from "@/domain/attachments";
import type { DraftForm, Expense } from "@/domain/model";
import { pendingSuggestions, type ExtractedField, type FieldSuggestion } from "@/domain/ocr/merge";
import { createDraftFromImage, deleteDraft, getAttachments, removeImage, setImage, updateDraftForm } from "@/data/attachments";
import { applyExtractionToDraft, getExtraction, readImage, readingProgress, subscribeReading } from "@/data/extraction";
import {
  findExpensesFlaggingAsDuplicate,
  keepPossibleDuplicate,
  PossibleDuplicateError,
  saveExpense,
  voidExpense,
  type DuplicateCandidate,
  type DuplicateDecision,
} from "@/data/ledger";
import { processImage } from "@/integrations/images/process-image";
import { tesseractEngine } from "@/integrations/ocr/tesseract";
import { formatDate, formatEurPlain, formatMonth } from "@/lib/format";
import { ACCOUNTING_RULE_LABELS } from "@/lib/labels";
import { DuplicateMatches } from "@/ui/DuplicateMatches";
import { AttachmentPreview, CaptureButtons } from "@/ui/ImageCapture";
import { ReceiptReading } from "@/ui/ReceiptReading";
import { Button, ConfirmButton, Field, Notice, PageHeader, Panel, SelectInput, TextArea, TextInput } from "@/ui/primitives";
import { db, useCategoryTree, usePaymentMethods, useSetting, useToday } from "../hooks";

// The form's values are exactly what a persisted draft stores.
type FormState = DraftForm;

function fromExpense(e: Expense): FormState {
  return {
    amount: e.amount.replace(".", ","),
    purchaseDate: e.purchaseDate,
    paymentMethodId: e.paymentMethodId ?? "",
    categoryId: e.categoryId,
    merchant: e.merchant ?? "",
    note: e.note ?? "",
    overrideEnabled: e.accountingRule === "manual_override",
    override: e.accountingRule === "manual_override" ? e.accountingMonth : "",
  };
}

export function ExpenseFormPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { today } = useToday();
  const methods = usePaymentMethods();
  const tree = useCategoryTree();
  const lastMethod = useSetting<string | null>("entry.lastPaymentMethodId", null);
  const existing = useLiveQuery(async () => (id ? ((await db.expenses.get(id)) ?? null) : null), [id]);
  // `?bozza=<id>`: completing a persisted draft (e.g. after "Fotografa ricevuta").
  const draftId = id ? null : params.get("bozza");
  const draft = useLiveQuery(async () => (draftId ? ((await db.expenseDrafts.get(draftId)) ?? null) : null), [draftId]);
  const owner = existing ? { kind: "expense" as const, id: existing.id } : draftId ? { kind: "draft" as const, id: draftId } : null;
  const attachment = useLiveQuery(
    async () => (owner ? ((await getAttachments(db, owner))[0] ?? null) : null),
    [owner?.kind, owner?.id],
  );
  // On-device OCR of the image: stored reading (live) and progress of a running read.
  const extraction = useLiveQuery(async () => (attachment ? await getExtraction(db, attachment.id) : null), [attachment?.id, attachment?.sha256]);
  const readProgress = useSyncExternalStore(subscribeReading, () => readingProgress(attachment?.id));
  const appliedRef = useRef<string | null>(null);
  const allMethods = usePaymentMethods(false);
  const allCategories = useCategoryTree(true);
  // Duplicate context of a saved expense: the expenses it matched, and those that matched it.
  const matchedExpenses = useLiveQuery(
    async () =>
      existing && existing.duplicate.matches.length
        ? (await db.expenses.bulkGet(existing.duplicate.matches.map((m) => m.expenseId))).filter((e) => e !== undefined)
        : [],
    [existing],
  );
  const flaggedBy = useLiveQuery(async () => (id ? findExpensesFlaggingAsDuplicate(db, id) : []), [id]);
  const amountRef = useRef<HTMLInputElement>(null);
  const warningRef = useRef<HTMLDivElement>(null);

  // The user's edits live in `edited`; until the first edit the form shows values derived from
  // the stored record (or defaults for a new expense).
  const [edited, setEdited] = useState<FormState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState<DuplicateCandidate[] | null>(null);
  const [imageBusy, setImageBusy] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  // On a phone the warning appears below the fold: bring it into view.
  useEffect(() => {
    if (duplicateWarning) warningRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [duplicateWarning]);

  // What a new expense starts with.
  const defaults = useMemo<FormState | null>(
    () =>
      methods
        ? {
            amount: "",
            purchaseDate: today,
            paymentMethodId: lastMethod && methods.some((m) => m.id === lastMethod) ? lastMethod : (methods[0]?.id ?? ""),
            categoryId: "",
            merchant: "",
            note: "",
            overrideEnabled: false,
            override: "",
          }
        : null,
    [methods, today, lastMethod],
  );
  const initial: FormState | null = !defaults ? null : existing ? fromExpense(existing) : id || draft === undefined ? null : (draft?.form ?? defaults);
  const form = edited ?? initial;

  // A draft with an image is read automatically (on the device; the result is stored).
  useEffect(() => {
    if (!draftId || !attachment || extraction !== null) return;
    readImage(db, attachment.id, tesseractEngine, { today }).catch(() => undefined);
  }, [draftId, attachment, extraction, today]);
  // Once read, the reading fills the draft's empty fields — once per reading, never over typing.
  useEffect(() => {
    if (!draftId || !defaults || extraction?.status !== "done" || extraction.appliedAt || appliedRef.current === extraction.id) return;
    appliedRef.current = extraction.id;
    void applyExtractionToDraft(db, draftId, extraction.id, defaults).then((applied) => {
      // The merged values are now in the draft: show them instead of the local copy.
      if (applied) setEdited(null);
    });
  }, [draftId, defaults, extraction]);

  if (existing === undefined) return <p className="text-muted">Caricamento…</p>;
  if (id && existing === null) return <Notice tone="error">Spesa non trovata.</Notice>;
  if (draftId && draft === null) {
    return (
      <div>
        <Notice tone="error">Bozza non trovata: potrebbe essere già stata salvata o eliminata.</Notice>
        <Link to="/spese" className="mt-4 inline-block font-semibold text-petrol">
          Vai alle spese
        </Link>
      </div>
    );
  }
  if (!form || !methods || !tree) return <p className="text-muted">Caricamento…</p>;

  const readOnly = existing?.source === "excel_import" || existing?.status === "void";
  const method = methods.find((m) => m.id === form.paymentMethodId);
  let preview: { month: string; rule: string } | null = null;
  try {
    if (method && form.purchaseDate) {
      const r = computeAccountingMonth({ purchaseDate: form.purchaseDate, rule: method.accountingRule, override: form.overrideEnabled ? form.override : null });
      preview = { month: r.accountingMonth, rule: ACCOUNTING_RULE_LABELS[r.appliedRule] };
    }
  } catch {
    preview = null;
  }

  async function persist(onDuplicate: DuplicateDecision) {
    if (!form) return;
    setError(null);
    setSaving(true);
    try {
      const saved = await saveExpense(
        db,
        {
          id: existing?.id,
          purchaseDate: form.purchaseDate,
          amount: form.amount,
          paymentMethodId: form.paymentMethodId,
          categoryId: form.categoryId,
          merchant: form.merchant,
          note: form.note,
          accountingMonthOverride: form.overrideEnabled ? form.override : null,
          draftId: existing ? undefined : (draftId ?? undefined),
        },
        { onDuplicate },
      );
      await db.settings.put({ key: "entry.lastPaymentMethodId", value: form.paymentMethodId });
      navigate(`/spese?mese=${saved.accountingMonth}`);
    } catch (e) {
      if (e instanceof PossibleDuplicateError) setDuplicateWarning(e.candidates);
      else setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    void persist("ask");
  }

  // Any change invalidates a shown duplicate warning: the next save checks again.
  const set = (patch: Partial<FormState>) => {
    setDuplicateWarning(null);
    const next = { ...form, ...patch };
    setEdited(next);
    // A draft keeps what was typed, so closing the app does not lose it.
    if (draftId) void updateDraftForm(db, draftId, next).catch(() => undefined);
  };

  // The image stays on the device: it is processed in the browser and stored in IndexedDB.
  async function addImage(file: File, origin: AttachmentOrigin) {
    if (!form) return;
    setImageError(null);
    setImageBusy(true);
    try {
      if (owner) await setImage(db, owner, file, origin, processImage);
      else {
        // A manual entry that gains an image becomes a draft, keeping what was already typed.
        const created = await createDraftFromImage(db, file, origin, processImage, form);
        navigate(`/spese/nuova?bozza=${created.draft.id}`, { replace: true });
      }
    } catch (e) {
      setImageError((e as Error).message);
    } finally {
      setImageBusy(false);
    }
  }
  const imageEditable = !existing || existing.status !== "void";

  const suggestions = !readOnly && form && extraction?.status === "done" ? pendingSuggestions(form, extraction.reading, extraction.suggestion) : [];
  const describeSuggestion = (s: FieldSuggestion) =>
    s.field === "amount"
      ? `${s.value} €`
      : s.field === "purchaseDate"
        ? formatDate(s.value)
        : s.field === "categoryId"
          ? categoryLabel(s.value)
          : s.field === "paymentMethodId"
            ? methodLabel(s.value)
            : s.value;
  /** "dalla ricevuta" next to a field still holding the value OCR filled in. */
  const fromReceipt = (field: ExtractedField, fallback?: string) =>
    extraction?.applied[field] !== undefined && extraction.applied[field] === String(form[field as keyof FormState]) ? (
      <span className="font-medium text-petrol">dalla ricevuta — verifica</span>
    ) : (
      fallback
    );
  const categoryLabel = (cid: string) => allCategories?.flatMap((g) => g.categories).find((c) => c.id === cid)?.excelLabel ?? "Categoria";
  const methodLabel = (mid: string | null) => (mid ? (allMethods?.find((m) => m.id === mid)?.name ?? "Metodo sconosciuto") : "metodo non indicato");

  return (
    <div>
      <PageHeader title={existing ? "Spesa" : "Nuova spesa"} description={draftId && !existing ? "Bozza salvata sul dispositivo: completa i dati e salva." : undefined} />
      {existing?.source === "excel_import" ? (
        <Notice>
          Questa riga viene dal file Excel (foglio Database, {existing.legacy?.cell}) e si modifica lì.
          {existing.legacy?.formula ? ` Formula originale: =${existing.legacy.formula}.` : ""}
          {!existing.purchaseDateCertain ? " La data potrebbe essere quella contabile (1° del mese) e non quella di acquisto." : ""}
        </Notice>
      ) : null}
      {existing?.status === "void" ? <Notice>Spesa annullata il {formatDate(existing.voidedAt!.slice(0, 10))}.</Notice> : null}

      {existing && existing.status !== "void" && existing.duplicate.status === "possible" ? (
        <div className="mt-3 flex flex-col gap-2 rounded-xl border border-over/40 bg-over-soft p-4">
          <p className="font-semibold text-over">Possibile spesa duplicata, da verificare.</p>
          <p className="text-sm">{existing.duplicate.matches.length === 1 ? "È simile a questa spesa:" : "È simile a queste spese:"}</p>
          <DuplicateMatches rows={(matchedExpenses ?? []).map((expense) => ({ expense, match: existing.duplicate.matches.find((m) => m.expenseId === expense.id) }))} categoryLabel={categoryLabel} methodLabel={methodLabel} />
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => void keepPossibleDuplicate(db, existing.id)}>Tieni questa spesa</Button>
            <ConfirmButton
              label="Annulla questa spesa"
              confirmLabel="Conferma annullamento"
              onConfirm={async () => {
                await voidExpense(db, existing.id);
                navigate(`/spese?mese=${existing.accountingMonth}`);
              }}
            />
          </div>
        </div>
      ) : null}
      {existing && existing.duplicate.status === "kept" ? (
        <div className="mt-3">
          <Notice>
            <p>Hai scelto di tenere questa spesa anche se è simile a:</p>
            <div className="mt-2">
              <DuplicateMatches rows={(matchedExpenses ?? []).map((expense) => ({ expense, match: existing.duplicate.matches.find((m) => m.expenseId === expense.id) }))} categoryLabel={categoryLabel} methodLabel={methodLabel} />
            </div>
          </Notice>
        </div>
      ) : null}
      {flaggedBy && flaggedBy.length > 0 ? (
        <div className="mt-3">
          <Notice>
            <p>
              {flaggedBy.length === 1
                ? "Un'altra spesa è stata salvata come possibile duplicato di questa:"
                : "Altre spese sono state salvate come possibili duplicati di questa:"}
            </p>
            <div className="mt-2">
              <DuplicateMatches rows={flaggedBy.map((expense) => ({ expense }))} categoryLabel={categoryLabel} methodLabel={methodLabel} />
            </div>
          </Notice>
        </div>
      ) : null}

      {existing?.source !== "excel_import" && (attachment || imageEditable) ? (
        <Panel className="mt-4" title={attachment ? "Immagine" : "Immagine (facoltativa)"}>
          {attachment ? (
            <AttachmentPreview
              attachment={attachment}
              busy={imageBusy}
              onReplace={imageEditable ? addImage : undefined}
              onRemove={imageEditable ? () => removeImage(db, attachment.id) : undefined}
            />
          ) : attachment === null ? (
            <CaptureButtons onFile={addImage} disabled={imageBusy} />
          ) : null}
          {attachment ? (
            <ReceiptReading
              extraction={extraction}
              progress={readProgress}
              suggestions={suggestions}
              describe={describeSuggestion}
              onUse={readOnly ? undefined : (sug) => set({ [sug.field]: sug.value } as Partial<FormState>)}
              onRead={readOnly ? undefined : (force) => void readImage(db, attachment.id, tesseractEngine, { today, force }).catch(() => undefined)}
            />
          ) : null}
          <div aria-live="polite">
            {imageBusy ? <p className="mt-2 text-sm text-muted">Preparazione dell&apos;immagine…</p> : null}
            {imageError ? (
              <div className="mt-2">
                <Notice tone="error">{imageError}</Notice>
              </div>
            ) : null}
          </div>
        </Panel>
      ) : null}

      <Panel className="mt-4">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <Field label="Importo (€)" hint={fromReceipt("amount")}>
            <TextInput
              inputMode="decimal"
              ref={amountRef}
              autoFocus={!existing && !draftId}
              required
              placeholder="0,00"
              value={form.amount}
              disabled={readOnly}
              onChange={(e) => set({ amount: e.target.value })}
              className="amount text-2xl font-semibold"
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Data di acquisto" hint={fromReceipt("purchaseDate")}>
              <TextInput type="date" required value={form.purchaseDate} disabled={readOnly} onChange={(e) => set({ purchaseDate: e.target.value })} />
            </Field>
            <Field label="Metodo di pagamento">
              <SelectInput required value={form.paymentMethodId} disabled={readOnly} onChange={(e) => set({ paymentMethodId: e.target.value })}>
                {existing && !existing.paymentMethodId ? <option value="">Non indicato (Excel)</option> : null}
                {methods.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </SelectInput>
            </Field>
          </div>

          <Field label="Categoria" hint={fromReceipt("categoryId")}>
            <SelectInput required value={form.categoryId} disabled={readOnly} onChange={(e) => set({ categoryId: e.target.value })}>
              <option value="" disabled>
                Scegli una categoria
              </option>
              {tree.map(({ group, categories }) => (
                <optgroup key={group.id} label={group.excelLabel}>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.excelLabel}
                    </option>
                  ))}
                </optgroup>
              ))}
            </SelectInput>
          </Field>
          {tree.length === 0 ? (
            <Notice>
              Non ci sono categorie: <Link to="/importa" className="font-semibold underline underline-offset-4">importale dal file Excel</Link>.
            </Notice>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Esercente" hint={fromReceipt("merchant", "Facoltativo")}>
              <TextInput value={form.merchant} disabled={readOnly} onChange={(e) => set({ merchant: e.target.value })} autoComplete="off" />
            </Field>
            <Field label="Nota" hint="Facoltativa">
              <TextArea value={form.note} disabled={readOnly} onChange={(e) => set({ note: e.target.value })} />
            </Field>
          </div>

          <div className="rounded-lg bg-petrol-soft px-4 py-3" aria-live="polite">
            {existing?.source === "excel_import" ? (
              <p>
                Mese contabile: <strong className="font-ledger text-lg">{formatMonth(existing.accountingMonth)}</strong> (dalla colonna Mese del file Excel)
              </p>
            ) : preview ? (
              <p>
                Conta in <strong className="font-ledger text-lg">{formatMonth(preview.month)}</strong>
                <span className="block text-sm text-muted">Regola: {preview.rule.toLowerCase()}.</span>
              </p>
            ) : (
              <p className="text-sm text-muted">Inserisci data e metodo di pagamento per vedere il mese contabile.</p>
            )}
            {!readOnly ? (
              <label className="mt-2 flex flex-wrap items-center gap-3 text-sm">
                <input type="checkbox" checked={form.overrideEnabled} onChange={(e) => set({ overrideEnabled: e.target.checked, override: e.target.checked ? (preview?.month ?? "") : "" })} />
                Scegli un altro mese contabile
                {form.overrideEnabled ? (
                  <input
                    type="month"
                    required
                    value={form.override}
                    onChange={(e) => set({ override: e.target.value })}
                    className="rounded-md border border-line bg-surface px-2 py-1"
                  />
                ) : null}
              </label>
            ) : null}
          </div>

          {error ? <Notice tone="error">{error}</Notice> : null}

          {duplicateWarning ? (
            <div ref={warningRef} role="alert" className="flex flex-col gap-3 rounded-xl border border-over/40 bg-over-soft p-4">
              <div>
                <p className="font-semibold text-over">Possibile spesa duplicata.</p>
                <p className="text-sm">
                  {duplicateWarning.length === 1 ? "Esiste già una spesa simile." : `Esistono già ${duplicateWarning.length} spese simili.`} Controlla prima di
                  salvare: la scelta è tua.
                </p>
              </div>
              <DuplicateMatches rows={duplicateWarning} categoryLabel={categoryLabel} methodLabel={methodLabel} linkTarget="_blank" />
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => void persist("keep")} disabled={saving}>
                  {existing ? "Salva comunque" : "Tieni la nuova spesa"}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setDuplicateWarning(null);
                    amountRef.current?.focus();
                  }}
                >
                  Modifica
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate(`/spese?mese=${existing ? existing.accountingMonth : (preview?.month ?? form.purchaseDate.slice(0, 7))}`)}
                >
                  {existing ? "Annulla le modifiche" : "Non salvare la spesa"}
                </Button>
              </div>
            </div>
          ) : null}

          {!readOnly && !duplicateWarning ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Button type="submit" disabled={saving} className="flex-1 sm:flex-none">
                {saving ? "Salvataggio…" : existing ? "Salva modifiche" : "Salva spesa"}
              </Button>
              {draftId && !existing ? (
                <ConfirmButton
                  label="Elimina bozza"
                  confirmLabel="Elimina bozza e immagine"
                  onConfirm={async () => {
                    await deleteDraft(db, draftId);
                    navigate("/spese");
                  }}
                />
              ) : null}
              {existing ? (
                <ConfirmButton
                  label="Annulla spesa"
                  confirmLabel="Conferma annullamento"
                  onConfirm={async () => {
                    await voidExpense(db, existing.id);
                    navigate(`/spese?mese=${existing.accountingMonth}`);
                  }}
                />
              ) : null}
            </div>
          ) : null}
          {existing ? (
            <p className="text-xs text-muted">
              Versione {existing.version}, importo registrato {formatEurPlain(existing.amountEur)}.
            </p>
          ) : null}
        </form>
      </Panel>
    </div>
  );
}
